import { wencesApi } from "@/api/wencesApi";

/**
 * Tipo de cambio de una operación (exchange_rates_module, ticket #397), compartido por las
 * pantallas que lo muestran: varias filas de gastos piden lo mismo y salen con un solo
 * pedido. Se recuerda unos segundos; después de fijar un tipo de cambio o guardar, se
 * invalida para leer el valor nuevo.
 */
const CACHE_MS = 15 * 1000;

/** @type {Map<string, { at: number, promise: Promise<Object> }>} */
const cache = new Map();

function idOf(value) {
  if (value === null || value === undefined || value === "") return "";
  return typeof value === "object" && value._id != null
    ? String(value._id)
    : String(value);
}

/**
 * @param {string} refId id del embarque
 * @param {Array} [extraCurrencyIds] monedas todavía no guardadas en la operación
 * @returns {Promise<{enabled: boolean, systemCurrencyId: string|null, rates: Array<Object>}>}
 *   enabled=false si el módulo está apagado o el usuario no tiene acceso: no se muestra nada.
 */
export function fetchOperationRates(refId, extraCurrencyIds = []) {
  const id = idOf(refId);
  if (!id)
    return Promise.resolve({
      enabled: false,
      systemCurrencyId: null,
      rates: [],
    });
  const extra = [...new Set(extraCurrencyIds.map(idOf).filter(Boolean))].sort();
  const key = `${id}|${extra.join(",")}`;
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < CACHE_MS) return hit.promise;

  const promise = wencesApi
    .get(`/api/exchange-rates/operations/shipping/${encodeURIComponent(id)}`, {
      params: extra.length ? { currencies: extra.join(",") } : undefined,
    })
    .then(({ data }) => ({
      enabled: !!(data && data.enabled),
      systemCurrencyId: (data && data.systemCurrencyId) || null,
      rates: Array.isArray(data && data.data) ? data.data : [],
    }))
    .catch(() => {
      cache.delete(key);
      return { enabled: false, systemCurrencyId: null, rates: [] };
    });
  cache.set(key, { at: Date.now(), promise });
  return promise;
}

/** Olvida lo recordado de una operación (o de todas). */
export function invalidateOperationRates(refId) {
  const id = idOf(refId);
  for (const key of [...cache.keys()]) {
    if (!id || key.startsWith(`${id}|`)) cache.delete(key);
  }
}

/**
 * Unidades de la moneda del sistema por 1 unidad de `currencyId` en la operación.
 * @returns {number|null} 1 para la moneda del sistema; null si falta la cotización
 */
export function operationRateFor(fx, currencyId) {
  const id = idOf(currencyId);
  if (!fx || !fx.enabled) return null;
  if (!id || id === String(fx.systemCurrencyId)) return 1;
  const entry = (fx.rates || []).find((r) => r && String(r.currencyId) === id);
  const rate = entry && Number(entry.rate);
  return Number.isFinite(rate) && rate > 0 ? rate : null;
}
